/**
 * Pure canvas drawing routine extracted from LiveCoach.jsx.
 *
 * Renders the AR mirror's per-step overlay onto the existing canvas
 * 2D context. Reads no React state — `activeZone` is passed in by the
 * caller (LiveCoach reads it from a ref so per-step changes take
 * effect even though the rAF loop captured this fn at init time).
 *
 * Three zone-type branches (matching faceLandmarks.js):
 *   - "perimeter": one continuous boundary around selected beard areas
 *   - "stroke":  smoothed quadratic-curve path (with optional
 *                flowing chevron arrows + STOP marker for sweep
 *                motion — the contour step)
 *   - else:      filled closed polygon
 *
 * Followed by a markers loop that renders breathing dots / lash
 * wisps / spiral animations on top of the soft fill.
 */
export function drawZoneOnCanvas(ctx, landmarks, fit, activeZone) {
  const { vw, vh, s, offX, offY } = fit;
  const breath = 0.5 + 0.5 * Math.sin(performance.now() / 700);
  const baseAlpha = 0.35 + 0.25 * breath;
  const t = performance.now();

  // Convert a normalised landmark (0-1 in raw video) → CSS-pixel
  // coordinate inside the canvas's displayed box, accounting for the
  // video's object-fit: cover scale and centre-crop offset.
  const project = (lm) => ({
    x: lm.x * vw * s - offX,
    y: lm.y * vh * s - offY,
  });

  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Brow product steps have their own precise, landmark-bound visual
  // language. Do not draw the legacy filled/stroked brow zone beneath
  // it: that doubled the guidance and made the new arrows look clipped.
  if (activeZone.browMode) {
    drawBrowTechnique(ctx, landmarks, project, s, t, activeZone.browMode);
    return;
  }

  // ── "perimeter" zone type — used by the user-customised
  //    color-corrector step. Samples the edge of every selected shadow
  //    area, then wraps them in one continuous convex boundary.
  if (activeZone.type === "perimeter") {
    const right = landmarks[234];
    const left = landmarks[454];
    const faceWidthPx =
      right && left
        ? Math.hypot((right.x - left.x) * vw * s, (right.y - left.y) * vh * s)
        : 200;
    const boundarySamples = [];
    for (const blob of activeZone.blobs || []) {
      const lm = landmarks[blob.center];
      if (!lm) continue;
      const c = project(lm);
      const r = Math.max(8, blob.radius * faceWidthPx);
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2;
        boundarySamples.push({
          x: c.x + Math.cos(angle) * r,
          y: c.y + Math.sin(angle) * r,
        });
      }
    }
    const perimeter = convexHull(boundarySamples);
    if (perimeter.length >= 3) {
      traceSmoothClosedPath(ctx, perimeter);
      ctx.shadowBlur = 28 * s;
      ctx.shadowColor = `rgba(213, 168, 164, ${0.55 + 0.25 * breath})`;
      ctx.fillStyle = `rgba(213, 168, 164, ${0.16 + 0.1 * breath})`;
      ctx.fill();
      ctx.shadowBlur = 14 * s;
      ctx.shadowColor = "rgba(245, 220, 215, 0.85)";
      ctx.strokeStyle = `rgba(255, 245, 240, ${0.68 + 0.2 * breath})`;
      ctx.lineWidth = 2.5 * s;
      ctx.setLineDash([10 * s, 7 * s]);
      ctx.lineDashOffset = -(t / 55) % (17 * s);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.shadowBlur = 0;
    }
  } else {
    for (const path of activeZone.paths || []) {
      const pts = path.indices
        .map((i) => landmarks[i])
        .filter(Boolean)
        .map(project);
      if (pts.length < 2) continue;

      // Build smoothed path via quadratic curves through midpoints
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length - 1; i++) {
        const mid = {
          x: (pts[i].x + pts[i + 1].x) / 2,
          y: (pts[i].y + pts[i + 1].y) / 2,
        };
        ctx.quadraticCurveTo(pts[i].x, pts[i].y, mid.x, mid.y);
      }
      ctx.quadraticCurveTo(
        pts[pts.length - 1].x,
        pts[pts.length - 1].y,
        pts[pts.length - 1].x,
        pts[pts.length - 1].y
      );

      if (activeZone.type === "stroke") {
        ctx.closePath = () => {}; // no-op for stroke paths
        const w = (path.width || 8) * s; // scale to displayed face size
        // Outer glow
        ctx.shadowBlur = 22 * s;
        ctx.shadowColor = `rgba(213, 168, 164, ${0.7 * breath + 0.3})`;
        ctx.strokeStyle = `rgba(245, 220, 215, ${baseAlpha + 0.25})`;
        ctx.lineWidth = w + 2 * s;
        ctx.stroke();
        // Inner sharper line
        ctx.shadowBlur = 0;
        ctx.strokeStyle = `rgba(255, 248, 244, ${0.85})`;
        ctx.lineWidth = w * 0.45;
        ctx.stroke();

        // ── Flowing chevron arrows + STOP marker ────────────────
        // Only for sweep strokes (contour). Arrows show the
        // direction of application along each path so beginners
        // don't sweep in the wrong direction; the STOP pill at the
        // path's terminus tells them where to lift the brush.
        if (activeZone.motion === "sweep" && pts.length >= 2) {
          drawSweepArrowsAndStop(ctx, pts, s, w, t);
        }
      } else {
        // Fill closed polygon
        ctx.closePath();
        // Soft outer glow ring
        ctx.shadowBlur = 28 * s;
        ctx.shadowColor = `rgba(213, 168, 164, ${0.55 + 0.25 * breath})`;
        ctx.fillStyle = `rgba(213, 168, 164, ${0.18 + 0.12 * breath})`;
        ctx.fill();
        // Beard-shadow softening needs an AREA cue, not decorative
        // sparkles. A warm, clipped wash travels from the centre of the
        // lower face toward both jawlines, demonstrating the order in which
        // to press a thin layer while keeping the whole coverage boundary
        // continuously visible and face-tracked.
        if (activeZone.markerStyle === "coverage_pat") {
          const xs = pts.map((p) => p.x);
          const ys = pts.map((p) => p.y);
          const minX = Math.min(...xs);
          const maxX = Math.max(...xs);
          const minY = Math.min(...ys);
          const maxY = Math.max(...ys);
          const centreX = (minX + maxX) / 2;
          const centreY = (minY + maxY) / 2;
          const travel = (0.5 + 0.5 * Math.sin(t / 900)) * (maxX - minX) * 0.42;
          const radius = Math.max(26 * s, (maxY - minY) * 0.48);
          ctx.save();
          ctx.clip();
          for (const direction of [-1, 1]) {
            const x = centreX + direction * travel;
            const wash = ctx.createRadialGradient(x, centreY, 0, x, centreY, radius);
            wash.addColorStop(0, "rgba(238, 157, 126, 0.34)");
            wash.addColorStop(0.48, "rgba(229, 145, 116, 0.18)");
            wash.addColorStop(1, "rgba(229, 145, 116, 0)");
            ctx.fillStyle = wash;
            ctx.fillRect(minX, minY, maxX - minX, maxY - minY);
          }
          ctx.restore();
        }
        // Edge highlight stroke
        ctx.shadowBlur = 12 * s;
        ctx.shadowColor = `rgba(245, 220, 215, ${0.8})`;
        ctx.strokeStyle = `rgba(255, 245, 240, ${0.55 + 0.2 * breath})`;
        ctx.lineWidth = 2.5 * s;
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    }
  }

  // ── Per-step "tap here" markers: pulsing dots / wisps / arrows on
  // top of the soft fill so adjacent steps that share a zone (e.g.
  // eyeliner vs mascara) still read as visibly distinct. The marker
  // style varies by `motion` type — same data, different vocabulary.
  const markers = activeZone.markers || [];
  if (markers.length) {
    drawMarkers(ctx, landmarks, project, activeZone, s, t);
  }

  // Foundation is stippled from the centre of the face toward its
  // perimeter. Render the movement as landmark-anchored dashed rays
  // with traveling chevrons, so the guidance stays attached to the
  // user's face while they turn or move closer to the camera.
  if (activeZone.motion === "stipple" && activeZone.flow === "outward") {
    drawStippleOutwardArrows(ctx, landmarks, project, s, t);
  }

}

// ───────────────────────────────────────────── private helpers

function convexHull(points) {
  if (points.length <= 3) return points;
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (o, a, b) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower = [];
  for (const point of sorted) {
    while (lower.length >= 2 && cross(lower.at(-2), lower.at(-1), point) <= 0) {
      lower.pop();
    }
    lower.push(point);
  }
  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const point = sorted[i];
    while (upper.length >= 2 && cross(upper.at(-2), upper.at(-1), point) <= 0) {
      upper.pop();
    }
    upper.push(point);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

function traceSmoothClosedPath(ctx, points) {
  const firstMid = {
    x: (points[0].x + points.at(-1).x) / 2,
    y: (points[0].y + points.at(-1).y) / 2,
  };
  ctx.beginPath();
  ctx.moveTo(firstMid.x, firstMid.y);
  for (let i = 0; i < points.length; i++) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    ctx.quadraticCurveTo(
      current.x,
      current.y,
      (current.x + next.x) / 2,
      (current.y + next.y) / 2,
    );
  }
  ctx.closePath();
}

function drawBrowMakeupPreview(ctx, landmarks, project, s, mode) {
  const brows = [
    {
      upper: [70, 63, 105, 66, 107],
      lower: [55, 65, 52, 53, 46],
      pairs: [[70, 46], [63, 53], [105, 52], [66, 65], [107, 55]],
    },
    {
      upper: [336, 296, 334, 293, 300],
      lower: [276, 283, 282, 295, 285],
      pairs: [[336, 276], [296, 283], [334, 282], [293, 295], [300, 285]],
    },
  ];
  const fillAlpha =
    mode === "pomade" ? 0.42 : mode === "hair_strokes" ? 0.25 : mode === "gel" ? 0.16 : 0.11;

  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.shadowBlur = 2 * s;
  ctx.shadowColor = "rgba(48, 27, 21, 0.18)";

  for (const brow of brows) {
    const upper = brow.upper.map((idx) => landmarks[idx]).filter(Boolean).map(project);
    const lower = brow.lower.map((idx) => landmarks[idx]).filter(Boolean).map(project);
    if (upper.length < 2 || lower.length < 2) continue;

    const all = [...upper, ...lower];
    const minX = Math.min(...all.map((p) => p.x));
    const maxX = Math.max(...all.map((p) => p.x));
    const faceMidX = landmarks[168] ? project(landmarks[168]).x : (minX + maxX) / 2;
    const innerX = Math.abs(minX - faceMidX) < Math.abs(maxX - faceMidX) ? minX : maxX;
    const tailX = innerX === minX ? maxX : minX;

    // Build product gradually: airy at the brow head and softly more
    // defined toward the tail. Multiply blending lets real skin and
    // existing brow texture show through the pigment.
    const pigment = ctx.createLinearGradient(innerX, 0, tailX, 0);
    pigment.addColorStop(0, `rgba(71, 43, 34, ${fillAlpha * 0.28})`);
    pigment.addColorStop(0.38, `rgba(67, 40, 31, ${fillAlpha * 0.68})`);
    pigment.addColorStop(1, `rgba(58, 34, 27, ${fillAlpha})`);
    ctx.globalCompositeOperation = "multiply";
    ctx.beginPath();
    ctx.moveTo(upper[0].x, upper[0].y);
    for (let i = 1; i < upper.length - 1; i++) {
      const next = upper[i + 1];
      ctx.quadraticCurveTo(upper[i].x, upper[i].y, (upper[i].x + next.x) / 2, (upper[i].y + next.y) / 2);
    }
    ctx.lineTo(upper[upper.length - 1].x, upper[upper.length - 1].y);
    lower.forEach((p) => ctx.lineTo(p.x, p.y));
    ctx.closePath();
    ctx.fillStyle = pigment;
    ctx.fill();

    // Pencil/pen/pomade previews use many fine, slightly varied hairs
    // rather than five obvious graphic strokes.
    if (mode === "hair_strokes" || mode === "pomade") {
      const pairPoints = brow.pairs.map(([upperIdx, lowerIdx]) => ({
        top: project(landmarks[upperIdx]),
        bottom: project(landmarks[lowerIdx]),
      }));
      const HAIRS = 15;
      for (let hair = 0; hair < HAIRS; hair++) {
        const u = hair / (HAIRS - 1);
        const scaled = u * (pairPoints.length - 1);
        const segment = Math.min(pairPoints.length - 2, Math.floor(scaled));
        const local = scaled - segment;
        const a = pairPoints[segment];
        const b = pairPoints[segment + 1];
        const top = {
          x: a.top.x + (b.top.x - a.top.x) * local,
          y: a.top.y + (b.top.y - a.top.y) * local,
        };
        const bottom = {
          x: a.bottom.x + (b.bottom.x - a.bottom.x) * local,
          y: a.bottom.y + (b.bottom.y - a.bottom.y) * local,
        };
        const naturalVariation = Math.sin(hair * 2.17) * Math.min(1.2, 0.8 * s);
        const direction = Math.sign(tailX - innerX) || 1;
        ctx.strokeStyle = `rgba(46, 27, 22, ${0.24 + u * (mode === "pomade" ? 0.34 : 0.24)})`;
        ctx.lineWidth = Math.min(1.25, Math.max(0.55, (0.68 + (hair % 3) * 0.08) * s));
        ctx.beginPath();
        ctx.moveTo(bottom.x + naturalVariation, bottom.y);
        ctx.quadraticCurveTo(
          (bottom.x + top.x) / 2 + direction * u * 1.8 * s,
          (bottom.y + top.y) / 2,
          top.x + direction * u * 2.4 * s + naturalVariation,
          top.y,
        );
        ctx.stroke();
      }
    } else {
      // Gel/wax: a restrained sheen communicates lifted, set hairs
      // without inventing extra pigment.
      ctx.globalCompositeOperation = "screen";
      ctx.strokeStyle = "rgba(255, 235, 226, 0.18)";
      ctx.lineWidth = Math.min(1.8, Math.max(1, 1.2 * s));
      ctx.beginPath();
      ctx.moveTo(upper[0].x, upper[0].y);
      upper.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    }
    ctx.globalCompositeOperation = "source-over";
  }
  ctx.restore();
}

function drawStippleOutwardArrows(ctx, landmarks, project, s, t) {
  // Each pair runs from a central application anchor to an outer
  // blending destination. The layout mirrors the product reference:
  // forehead fans upward/outward, cheeks move toward the ears, and
  // the mouth/chin area moves toward the jaw.
  const rays = [
    [9, 10],
    [9, 103],
    [9, 332],
    [50, 234],
    [280, 454],
    [18, 132],
    [18, 361],
    [152, 172],
    [152, 397],
  ];
  const phase = (t / 1250) % 1;

  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (let i = 0; i < rays.length; i++) {
    const fromLm = landmarks[rays[i][0]];
    const toLm = landmarks[rays[i][1]];
    if (!fromLm || !toLm) continue;

    const from = project(fromLm);
    const to = project(toLm);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.hypot(dx, dy);
    if (distance < 4) continue;

    const nx = dx / distance;
    const ny = dy / distance;
    const px = -ny;
    const py = nx;

    // Dashed guide: visible enough to establish the technique, but
    // translucent so skin texture and existing makeup remain visible.
    ctx.setLineDash([5 * s, 7 * s]);
    ctx.lineDashOffset = -phase * 12 * s;
    ctx.strokeStyle = "rgba(255, 248, 244, 0.78)";
    ctx.lineWidth = Math.max(1.2, 1.6 * s);
    ctx.shadowBlur = 7 * s;
    ctx.shadowColor = "rgba(245, 220, 215, 0.82)";
    ctx.beginPath();
    ctx.moveTo(from.x + nx * 8 * s, from.y + ny * 8 * s);
    ctx.lineTo(to.x - nx * 5 * s, to.y - ny * 5 * s);
    ctx.stroke();

    // A moving chevron makes the outward direction unmistakable.
    const local = (phase + i * 0.09) % 1;
    const u = 0.16 + local * 0.72;
    const x = from.x + dx * u;
    const y = from.y + dy * u;
    const size = Math.max(5, 6.5 * s);
    const tipX = x + nx * size;
    const tipY = y + ny * size;
    const tailX = x - nx * size * 0.7;
    const tailY = y - ny * size * 0.7;
    ctx.setLineDash([]);
    ctx.strokeStyle = "rgba(255, 252, 248, 0.98)";
    ctx.lineWidth = Math.max(1.5, 2 * s);
    ctx.shadowBlur = 10 * s;
    ctx.shadowColor = "rgba(255, 232, 218, 0.95)";
    ctx.beginPath();
    ctx.moveTo(tailX + px * size * 0.7, tailY + py * size * 0.7);
    ctx.lineTo(tipX, tipY);
    ctx.lineTo(tailX - px * size * 0.7, tailY - py * size * 0.7);
    ctx.stroke();
  }

  ctx.restore();
}

function drawBrowTechnique(ctx, landmarks, project, s, t, mode) {
  const browSets = [
    [70, 63, 105, 66, 107],
    [336, 296, 334, 293, 300],
  ];
  const faceCenter = landmarks[168] ? project(landmarks[168]).x : null;
  const phase = (t / 1250) % 1;

  const drawArrow = (from, to, alpha = 1, width = 1.8) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = dx / len;
    const ny = dy / len;
    const px = -ny;
    const py = nx;
    // Keep the whole arrowhead inside the narrow brow band even when
    // object-fit scaling makes the face large on screen.
    const size = Math.min(4.5, Math.max(2.8, 3.4 * s));
    ctx.strokeStyle = `rgba(255, 248, 244, ${alpha})`;
    ctx.lineWidth = Math.max(1.2, width * s);
    ctx.shadowBlur = 7 * s;
    ctx.shadowColor = `rgba(245, 220, 215, ${0.8 * alpha})`;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(to.x - nx * size + px * size * 0.6, to.y - ny * size + py * size * 0.6);
    ctx.lineTo(to.x, to.y);
    ctx.lineTo(to.x - nx * size - px * size * 0.6, to.y - ny * size - py * size * 0.6);
    ctx.stroke();
  };

  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const indices of browSets) {
    // MediaPipe's upper brow contour is stable during head turns. A
    // small downward inset places the cue over the hairs themselves.
    const browInset = Math.min(5, 3.5 * s);
    const points = indices
      .map((idx) => landmarks[idx])
      .filter(Boolean)
      .map(project)
      .map((p) => ({ x: p.x, y: p.y + browInset }));
    if (points.length < 2) continue;

    if (mode === "map") {
      const ordered = [...points].sort(
        (a, b) => Math.abs(a.x - faceCenter) - Math.abs(b.x - faceCenter),
      );
      const head = ordered[0];
      const tail = ordered[ordered.length - 1];
      const arch = ordered.reduce((highest, point) =>
        point.y < highest.y ? point : highest,
      ordered[Math.floor(ordered.length / 2)]);
      const guides = [head, arch, tail];
      const reveal = (t / 2200) % 1;
      const pencilCycle = (t / 2700) % 1;
      const activeGuide = Math.min(2, Math.floor(pencilCycle * 3));
      const tapPhase = pencilCycle * 3 - activeGuide;

      // A temporary thread introduces the relationship between the
      // three landmarks, then disappears so it never becomes a hard
      // outline that invites the user to colour in the whole brow.
      if (reveal < 0.62) {
        ctx.setLineDash([Math.min(4, 3 * s), Math.min(5, 4 * s)]);
        ctx.lineDashOffset = -reveal * Math.min(18, 14 * s);
        ctx.strokeStyle = `rgba(255, 248, 244, ${0.58 * (1 - reveal / 0.62)})`;
        ctx.lineWidth = Math.max(1.1, 1.35 * s);
        ctx.shadowBlur = 6 * s;
        ctx.shadowColor = "rgba(245, 220, 215, 0.72)";
        ctx.beginPath();
        ctx.moveTo(head.x, head.y);
        ctx.quadraticCurveTo(arch.x, arch.y - 1.5 * s, tail.x, tail.y);
        ctx.stroke();
      }

      ctx.setLineDash([]);
      guides.forEach((guide, index) => {
        const pulse = 0.5 + 0.5 * Math.sin(t / 420 + index * 0.8);
        const radius = Math.min(4.8, Math.max(3, (3.1 + pulse * 0.65) * s));
        ctx.shadowBlur = (7 + pulse * 4) * s;
        ctx.shadowColor = "rgba(255, 226, 214, 0.92)";
        ctx.fillStyle = "rgba(255, 250, 246, 0.96)";
        ctx.beginPath();
        ctx.arc(guide.x, guide.y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.fillStyle = "rgba(181, 118, 133, 0.94)";
        ctx.beginPath();
        ctx.arc(guide.x, guide.y, Math.max(1.1, 1.3 * s), 0, Math.PI * 2);
        ctx.fill();
      });

      // A miniature brow pencil visits each landmark in sequence. Its
      // sharpened tip—not the barrel—touches the guide point, making
      // the three-point mapping action immediately understandable.
      // The handle hangs below and away from the face like a pencil
      // held in the user's hand, rather than lying across the brow.
      const target = guides[activeGuide];
      const outward = Math.sign(target.x - faceCenter) || 1;
      const tapEase = Math.sin(Math.min(1, tapPhase / 0.72) * Math.PI);
      const tip = {
        x: target.x + outward * (1 - tapEase) * Math.min(7, 5.5 * s),
        y: target.y + (1 - tapEase) * Math.min(7, 5.5 * s),
      };
      const shaftLength = Math.min(32, Math.max(23, 25 * s));
      const angle = outward > 0 ? Math.PI * 0.7 : Math.PI * 0.3;
      const ux = Math.cos(angle);
      const uy = Math.sin(angle);
      const px = -uy;
      const py = ux;
      const woodLength = Math.min(7, Math.max(5, 5.5 * s));
      const halfWidth = Math.min(3.4, Math.max(2.3, 2.6 * s));
      const woodBase = {
        x: tip.x + ux * woodLength,
        y: tip.y + uy * woodLength,
      };
      const handleEnd = {
        x: tip.x + ux * shaftLength,
        y: tip.y + uy * shaftLength,
      };

      ctx.save();
      ctx.setLineDash([]);
      ctx.shadowBlur = 7 * s;
      ctx.shadowColor = "rgba(36, 24, 24, 0.34)";

      // Rose pencil barrel.
      ctx.strokeStyle = "rgba(181, 118, 133, 0.98)";
      ctx.lineWidth = halfWidth * 2;
      ctx.beginPath();
      ctx.moveTo(woodBase.x, woodBase.y);
      ctx.lineTo(handleEnd.x, handleEnd.y);
      ctx.stroke();
      ctx.strokeStyle = "rgba(255, 224, 218, 0.72)";
      ctx.lineWidth = Math.max(0.8, 0.9 * s);
      ctx.beginPath();
      ctx.moveTo(woodBase.x + px * halfWidth * 0.45, woodBase.y + py * halfWidth * 0.45);
      ctx.lineTo(handleEnd.x + px * halfWidth * 0.45, handleEnd.y + py * halfWidth * 0.45);
      ctx.stroke();

      // Exposed wood and dark cosmetic core make the touching end
      // unmistakably read as a sharpened brow pencil.
      ctx.fillStyle = "rgba(239, 211, 181, 0.98)";
      ctx.beginPath();
      ctx.moveTo(tip.x, tip.y);
      ctx.lineTo(woodBase.x + px * halfWidth, woodBase.y + py * halfWidth);
      ctx.lineTo(woodBase.x - px * halfWidth, woodBase.y - py * halfWidth);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = "rgba(255, 244, 230, 0.76)";
      ctx.lineWidth = Math.max(0.65, 0.8 * s);
      ctx.stroke();
      ctx.fillStyle = "rgba(69, 43, 38, 0.98)";
      ctx.beginPath();
      ctx.arc(tip.x, tip.y, Math.max(0.9, 1.05 * s), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else if (mode === "hair_strokes") {
      // Teach the actual pencil gesture instead of decorating the brow
      // with arrows. One tapered hair appears at a time: touch, flick,
      // lift. Positions are sampled from head to tail and stay inside
      // the natural brow footprint.
      const ordered = [...points].sort(
        (a, b) => Math.abs(a.x - faceCenter) - Math.abs(b.x - faceCenter),
      );
      const sampleAlongBrow = (progress) => {
        const scaled = progress * (ordered.length - 1);
        const segment = Math.min(ordered.length - 2, Math.floor(scaled));
        const local = scaled - segment;
        return {
          x: ordered[segment].x + (ordered[segment + 1].x - ordered[segment].x) * local,
          y: ordered[segment].y + (ordered[segment + 1].y - ordered[segment].y) * local,
        };
      };
      const positions = [0.18, 0.5, 0.82];
      const cycle = (t / 1650) % 1;
      const activeStroke = Math.min(2, Math.floor(cycle * 3));
      const strokePhase = cycle * 3 - activeStroke;
      const root = sampleAlongBrow(positions[activeStroke]);
      const outward = Math.sign(root.x - faceCenter) || 1;
      const browProgress = positions[activeStroke];
      const lift = Math.min(9, (7.5 - browProgress * 2.5) * s);
      const drift = Math.min(8, (1.2 + browProgress * 5.8) * s) * outward;
      const growth = strokePhase < 0.72 ? strokePhase / 0.72 : 1;
      const fade = strokePhase < 0.78 ? 1 : Math.max(0, 1 - (strokePhase - 0.78) / 0.22);
      const tip = {
        x: root.x + drift * growth,
        y: root.y - lift * growth,
      };

      ctx.setLineDash([]);
      ctx.strokeStyle = `rgba(255, 250, 246, ${0.94 * fade})`;
      ctx.lineWidth = Math.max(0.9, 1.15 * s * (1 - growth * 0.28));
      ctx.shadowBlur = 5 * s;
      ctx.shadowColor = `rgba(245, 220, 215, ${0.82 * fade})`;
      ctx.beginPath();
      ctx.moveTo(root.x, root.y + Math.min(1.5, 1.1 * s));
      ctx.quadraticCurveTo(
        root.x + drift * growth * 0.42,
        root.y - lift * growth * 0.48,
        tip.x,
        tip.y,
      );
      ctx.stroke();

      // Contact cue at the root, followed by a tiny lift sparkle at
      // the tapered endpoint. Neither persists long enough to clutter.
      if (strokePhase < 0.22) {
        const contactAlpha = 1 - strokePhase / 0.22;
        ctx.fillStyle = `rgba(181, 118, 133, ${0.88 * contactAlpha})`;
        ctx.beginPath();
        ctx.arc(root.x, root.y, Math.max(1.5, 2 * s), 0, Math.PI * 2);
        ctx.fill();
      } else if (strokePhase > 0.76) {
        const sparkle = Math.max(0, 1 - (strokePhase - 0.76) / 0.24);
        const size = Math.min(4.5, 3.4 * s) * sparkle;
        ctx.strokeStyle = `rgba(255, 250, 246, ${sparkle})`;
        ctx.lineWidth = Math.max(0.8, 1.1 * s);
        ctx.beginPath();
        ctx.moveTo(tip.x - size, tip.y); ctx.lineTo(tip.x + size, tip.y);
        ctx.moveTo(tip.x, tip.y - size); ctx.lineTo(tip.x, tip.y + size);
        ctx.stroke();
      }
    } else if (mode === "gel") {
      // Both clear and tinted gel use the same feathering gesture:
      // lift from the roots at the brow head, then gradually turn the
      // hairs outward as the wand travels toward the tail. Keep all
      // arrowheads steadily visible; the gentle traveling wave supplies
      // motion without making the cues flash.
      const ordered = [...points].sort(
        (a, b) => Math.abs(a.x - faceCenter) - Math.abs(b.x - faceCenter),
      );
      const pointAt = (progress) => {
        const scaled = progress * (ordered.length - 1);
        const segment = Math.min(ordered.length - 2, Math.floor(scaled));
        const local = scaled - segment;
        return {
          x: ordered[segment].x + (ordered[segment + 1].x - ordered[segment].x) * local,
          y: ordered[segment].y + (ordered[segment + 1].y - ordered[segment].y) * local,
        };
      };

      [0.12, 0.38, 0.64, 0.88].forEach((progress, index) => {
        const root = pointAt(progress);
        const outward = Math.sign(root.x - faceCenter) || 1;
        const lift = Math.min(10, (8.5 - progress * 2.2) * s);
        const turn = Math.min(8, (1.2 + progress * 5.8) * s) * outward;
        const wave = 0.5 + 0.5 * Math.sin(t / 360 - index * 0.85);
        const travel = wave * Math.min(2.4, 1.9 * s);
        const length = Math.hypot(turn, -lift) || 1;
        const nx = turn / length;
        const ny = -lift / length;
        const from = {
          x: root.x + nx * travel,
          y: root.y + Math.min(2.5, 1.9 * s) + ny * travel,
        };
        const to = {
          x: root.x + turn + nx * travel,
          y: root.y - lift + ny * travel,
        };
        ctx.setLineDash([]);
        drawArrow(from, to, 0.88, 1.65);
      });
      ctx.setLineDash([]);
    } else if (mode === "wax") {
      // MediaPipe landmark density is uneven—especially on the left
      // brow—so using the raw five points can bunch several arrows at
      // one end. Reorder from brow head to tail, then sample by equal
      // arc length to keep all five cues visually separated.
      const ordered = [...points].sort(
        (a, b) => Math.abs(a.x - faceCenter) - Math.abs(b.x - faceCenter),
      );
      const cumulative = [0];
      let totalLength = 0;
      for (let i = 1; i < ordered.length; i++) {
        totalLength += Math.hypot(
          ordered[i].x - ordered[i - 1].x,
          ordered[i].y - ordered[i - 1].y,
        );
        cumulative.push(totalLength);
      }
      const evenlySpaced = Array.from({ length: 5 }, (_, index) => {
        const target = totalLength * ((index + 0.4) / 4.8);
        let segment = 1;
        while (segment < cumulative.length && cumulative[segment] < target) segment++;
        segment = Math.min(segment, ordered.length - 1);
        const startLength = cumulative[segment - 1];
        const segmentLength = cumulative[segment] - startLength || 1;
        const u = Math.max(0, Math.min(1, (target - startLength) / segmentLength));
        return {
          x: ordered[segment - 1].x + (ordered[segment].x - ordered[segment - 1].x) * u,
          y: ordered[segment - 1].y + (ordered[segment].y - ordered[segment - 1].y) * u,
        };
      });

      evenlySpaced.forEach((p, index) => {
        const outward = faceCenter == null ? (index < points.length / 2 ? -1 : 1) : Math.sign(p.x - faceCenter) || 1;
        const progress = index / Math.max(1, evenlySpaced.length - 1);
        // Several short arrows sit directly over the brow hairs. The
        // inner arrows lift; the outer arrows gradually turn toward the
        // tail, without projecting beyond the anatomical brow.
        const lift = Math.min(10, 8 * s);
        const drift = Math.min(6, 4.5 * s) * progress * outward;
        const stagger = (phase + index * 0.13) % 1;
        const travel = Math.sin((phase + index * 0.17) * Math.PI * 2) * Math.min(1.5, 1.2 * s);
        const start = { x: p.x - drift * 0.2, y: p.y + Math.min(2.5, 2 * s) + travel };
        const end = { x: p.x + drift, y: p.y - lift + travel };
        ctx.setLineDash([Math.min(3, 2.5 * s), Math.min(3.5, 3 * s)]);
        ctx.lineDashOffset = -phase * Math.min(9, 8 * s);
        drawArrow(start, end, 0.68 + 0.3 * stagger, 2);
      });
    } else if (mode === "blend") {
      // A broad spoolie pass travels from the inner brow upward and
      // outward to soften pencil marks after application.
      const ordered = [...points].sort((a, b) =>
        Math.abs(a.x - faceCenter) - Math.abs(b.x - faceCenter),
      );
      const segment = Math.min(ordered.length - 2, Math.floor(phase * (ordered.length - 1)));
      const a = ordered[Math.max(0, segment)];
      const b = ordered[Math.min(ordered.length - 1, segment + 1)];
      const local = (phase * (ordered.length - 1)) % 1;
      const pathX = a.x + (b.x - a.x) * local;
      const pathY = a.y + (b.y - a.y) * local;
      // Match the physical blending gesture: the spoolie travels from
      // brow head to tail while repeatedly brushing straight up and
      // down through the hairs. The brush head stays on the brow and
      // the handle points down, as it would in the user's hand.
      const strokePhase = (t / 620) % 1;
      const verticalBrush = Math.cos(strokePhase * Math.PI * 2) * Math.min(6, 4.8 * s);
      const p = {
        x: pathX,
        y: pathY + verticalBrush,
      };
      const headHalf = Math.min(10, Math.max(8, 8 * s));
      const handleLength = Math.min(24, Math.max(18, 19 * s));
      const maxBristle = Math.min(5, Math.max(3.8, 4 * s));

      // Draw a recognizable upright spoolie—not an abstract bar. The
      // oval bristle head crosses the brow vertically; the neck and
      // full handle extend beneath it toward the user's hand.
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.setLineDash([]);
      ctx.shadowBlur = 0;

      // Rose handle with a bright neck makes the tool silhouette read
      // immediately, even against very light or very dark skin.
      ctx.strokeStyle = "rgba(181, 118, 133, 0.98)";
      ctx.lineWidth = 5.2;
      ctx.beginPath();
      ctx.moveTo(0, headHalf + 4);
      ctx.lineTo(0, headHalf + handleLength);
      ctx.stroke();
      ctx.strokeStyle = "rgba(255, 248, 244, 0.98)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(0, headHalf);
      ctx.lineTo(0, headHalf + 4);
      ctx.stroke();

      // Central wire through the spoolie head.
      ctx.lineWidth = Math.min(1.8, Math.max(1.1, 1.25 * s));
      ctx.beginPath();
      ctx.moveTo(0, -headHalf);
      ctx.lineTo(0, headHalf);
      ctx.stroke();

      // Tapered bristles create the familiar oval/mascara-wand shape.
      const BRISTLES = 7;
      for (let tooth = 0; tooth < BRISTLES; tooth++) {
        const u = tooth / (BRISTLES - 1);
        const y = -headHalf + u * headHalf * 2;
        const oval = Math.sin(u * Math.PI);
        const bristle = Math.max(1.8, maxBristle * oval);
        ctx.lineWidth = 1.15;
        ctx.beginPath();
        ctx.moveTo(-bristle, y - 1.5);
        ctx.lineTo(bristle, y + 1.5);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(-bristle, y + 1.5);
        ctx.lineTo(bristle, y - 1.5);
        ctx.stroke();
      }
      ctx.restore();
    } else if (mode === "pomade") {
      // A controlled lower-edge pass from arch toward tail.
      const ordered = [...points].sort((a, b) => Math.abs(a.x - faceCenter) - Math.abs(b.x - faceCenter));
      ctx.setLineDash([6 * s, 6 * s]);
      ctx.lineDashOffset = -phase * 18 * s;
      ctx.strokeStyle = "rgba(255, 248, 244, 0.9)";
      ctx.lineWidth = Math.max(2, 2.6 * s);
      ctx.beginPath();
      ctx.moveTo(ordered[1].x, ordered[1].y + 6 * s);
      for (let i = 2; i < ordered.length; i++) {
        ctx.lineTo(ordered[i].x, ordered[i].y + 6 * s);
      }
      ctx.stroke();
      ctx.setLineDash([]);
      drawArrow(
        { x: ordered[ordered.length - 2].x, y: ordered[ordered.length - 2].y + 6 * s },
        { x: ordered[ordered.length - 1].x, y: ordered[ordered.length - 1].y + 6 * s },
        0.96,
        1.8,
      );
    }
  }

  ctx.restore();
}

function drawSweepArrowsAndStop(ctx, pts, s, w, t) {
  // Arc-length table for uniform sampling
  const segLen = [0];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    total += Math.hypot(dx, dy);
    segLen.push(total);
  }
  if (total <= 1) return;

  // Locate point + tangent at parameter u ∈ [0, 1]
  const pointAt = (u) => {
    const target = u * total;
    let k = 1;
    while (k < segLen.length && segLen[k] < target) k++;
    if (k >= segLen.length) k = segLen.length - 1;
    const segL = segLen[k] - segLen[k - 1] || 1;
    const f = (target - segLen[k - 1]) / segL;
    const x = pts[k - 1].x + (pts[k].x - pts[k - 1].x) * f;
    const y = pts[k - 1].y + (pts[k].y - pts[k - 1].y) * f;
    const tx = (pts[k].x - pts[k - 1].x) / segL;
    const ty = (pts[k].y - pts[k - 1].y) / segL;
    return { x, y, tx, ty };
  };

  // 3 chevrons per path; cycle length ~1.4s. Each chevron travels
  // from u=0.05 → u=0.88 (stops short of the STOP marker so it
  // doesn't overlap).
  const CHEV_COUNT = 3;
  const cyclePhase = (t / 1400) % 1;
  for (let i = 0; i < CHEV_COUNT; i++) {
    const local = (cyclePhase + i / CHEV_COUNT) % 1;
    const u = 0.05 + local * 0.83;
    // Fade in/out at the endpoints of the arrow's life
    const fade =
      local < 0.12
        ? local / 0.12
        : local > 0.88
        ? (1 - local) / 0.12
        : 1;
    const { x, y, tx, ty } = pointAt(u);
    const len = Math.hypot(tx, ty) || 1;
    const nx = tx / len;
    const ny = ty / len;
    // Perpendicular for chevron wings
    const px = -ny;
    const py = nx;
    const size = Math.max(4, w * 0.55);
    // Chevron: V-shape pointing in travel direction
    const tipX = x + nx * size * 0.6;
    const tipY = y + ny * size * 0.6;
    const leftX = x - nx * size * 0.35 + px * size * 0.55;
    const leftY = y - ny * size * 0.35 + py * size * 0.55;
    const rightX = x - nx * size * 0.35 - px * size * 0.55;
    const rightY = y - ny * size * 0.35 - py * size * 0.55;
    ctx.shadowBlur = 8 * s;
    ctx.shadowColor = `rgba(255, 230, 215, ${0.9 * fade})`;
    ctx.strokeStyle = `rgba(255, 240, 225, ${0.95 * fade})`;
    ctx.lineWidth = Math.max(1.4, 1.8 * s);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(leftX, leftY);
    ctx.lineTo(tipX, tipY);
    ctx.lineTo(rightX, rightY);
    ctx.stroke();
    ctx.shadowBlur = 0;
  }

  // STOP marker at path terminus. Pulsing ring + tiny "STOP" label.
  // Rendered once per path regardless of chevron cycle.
  const end = pts[pts.length - 1];
  const stopR = Math.max(6, w * 0.45);
  const stopPulse = 0.5 + 0.5 * Math.sin(t / 420);
  // Halo
  ctx.shadowBlur = 14 * s;
  ctx.shadowColor = `rgba(155, 106, 108, ${0.65 + 0.2 * stopPulse})`;
  ctx.fillStyle = `rgba(155, 106, 108, ${0.75 + 0.2 * stopPulse})`;
  ctx.beginPath();
  ctx.arc(end.x, end.y, stopR, 0, Math.PI * 2);
  ctx.fill();
  // Inner white dot
  ctx.shadowBlur = 0;
  ctx.fillStyle = "rgba(255, 250, 246, 0.96)";
  ctx.beginPath();
  ctx.arc(end.x, end.y, stopR * 0.45, 0, Math.PI * 2);
  ctx.fill();
  // Label — small "STOP" caps, offset so it doesn't overlap the
  // previous chevron's path. The offset is along the *outward*
  // tangent direction.
  const tail = pts[pts.length - 2];
  const tlx = end.x - tail.x;
  const tly = end.y - tail.y;
  const tl = Math.hypot(tlx, tly) || 1;
  const lx = end.x + (tlx / tl) * (stopR + 4 * s);
  const ly = end.y + (tly / tl) * (stopR + 4 * s);
  ctx.save();
  ctx.shadowBlur = 6 * s;
  ctx.shadowColor = "rgba(26, 20, 22, 0.6)";
  ctx.fillStyle = "rgba(255, 250, 246, 0.95)";
  ctx.font = `600 ${Math.max(9, 10 * s)}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("STOP", lx, ly);
  ctx.restore();
}

function drawMarkers(ctx, landmarks, project, activeZone, s, t) {
  const markers = activeZone.markers || [];
  const motion = activeZone.motion || "tap";
  const pulse = 0.5 + 0.5 * Math.sin(t / 500);
  const dotR = 6 * s + 2 * s * pulse;

  for (let markerIndex = 0; markerIndex < markers.length; markerIndex++) {
    const idx = markers[markerIndex];
    const lm = landmarks[idx];
    if (!lm) continue;
    const p = project(lm);

    if (activeZone.markerStyle === "circularSparkle") {
      // Blush: anchor on the cheek apple, circle there, then let the
      // moving ring carry the user's eye outward toward the temple.
      const cycle = (t / 1500 + markerIndex * 0.12) % 1;
      const angle = cycle * Math.PI * 2;
      const radius = 18 * s;
      const twinkle = 0.5 + 0.5 * Math.sin(t / 420 + markerIndex);

      ctx.save();
      ctx.setLineDash([3.5 * s, 5 * s]);
      ctx.lineDashOffset = -cycle * 10 * s;
      ctx.strokeStyle = "rgba(255, 248, 244, 0.88)";
      ctx.lineWidth = Math.max(1.2, 1.6 * s);
      ctx.shadowBlur = 7 * s;
      ctx.shadowColor = "rgba(245, 220, 215, 0.82)";
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      ctx.stroke();

      const ax = p.x + Math.cos(angle) * radius;
      const ay = p.y + Math.sin(angle) * radius;
      const tx = -Math.sin(angle);
      const ty = Math.cos(angle);
      const sideX = -ty;
      const sideY = tx;
      const arrowSize = 5.5 * s;
      ctx.setLineDash([]);
      ctx.strokeStyle = "rgba(255, 252, 248, 0.98)";
      ctx.lineWidth = Math.max(1.5, 2 * s);
      ctx.shadowBlur = 10 * s;
      ctx.beginPath();
      ctx.moveTo(
        ax - tx * arrowSize + sideX * arrowSize * 0.65,
        ay - ty * arrowSize + sideY * arrowSize * 0.65,
      );
      ctx.lineTo(ax + tx * arrowSize * 0.7, ay + ty * arrowSize * 0.7);
      ctx.lineTo(
        ax - tx * arrowSize - sideX * arrowSize * 0.65,
        ay - ty * arrowSize - sideY * arrowSize * 0.65,
      );
      ctx.stroke();

      const vertical = (7.5 + 2.5 * twinkle) * s;
      const horizontal = (5.5 + 1.8 * twinkle) * s;
      const waist = Math.max(1.5, 2.1 * s);
      ctx.fillStyle = "rgba(255, 252, 248, 0.98)";
      ctx.beginPath();
      ctx.moveTo(p.x, p.y - vertical);
      ctx.quadraticCurveTo(p.x + waist, p.y - waist, p.x + horizontal, p.y);
      ctx.quadraticCurveTo(p.x + waist, p.y + waist, p.x, p.y + vertical);
      ctx.quadraticCurveTo(p.x - waist, p.y + waist, p.x - horizontal, p.y);
      ctx.quadraticCurveTo(p.x - waist, p.y - waist, p.x, p.y - vertical);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    } else if (activeZone.markerStyle === "coverage_pat") {
      // Sequential fingertip/brush presses across the beard-coverage area.
      // The expanding ring shows a pat-and-lift action; there are no
      // sparkle crosses, so the cue reads as placement rather than shimmer.
      const localPhase = (t / 1500 + markerIndex / Math.max(1, markers.length)) % 1;
      const press = Math.max(0, 1 - Math.abs(localPhase - 0.28) / 0.28);
      const ringProgress = Math.min(1, localPhase / 0.62);
      const coreR = Math.max(3.5, (4.5 + press * 2.5) * s);
      const ringR = coreR + ringProgress * 8 * s;
      ctx.save();
      ctx.fillStyle = `rgba(239, 166, 136, ${0.48 + 0.34 * press})`;
      ctx.shadowBlur = (7 + 8 * press) * s;
      ctx.shadowColor = "rgba(241, 174, 145, 0.9)";
      ctx.beginPath();
      ctx.arc(p.x, p.y, coreR, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = `rgba(255, 239, 229, ${0.72 * (1 - ringProgress)})`;
      ctx.lineWidth = Math.max(1.2, 1.7 * s);
      ctx.beginPath();
      ctx.arc(p.x, p.y, ringR, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    } else if (activeZone.markerStyle === "sparkle") {
      // Three points beneath each eye twinkle in sequence from the
      // inner corner outward, matching the placement order in the
      // coaching copy. Each sparkle remains landmark-anchored.
      const localPhase = (t / 1050 + (markerIndex % 3) * 0.18) % 1;
      const twinkle = 0.5 + 0.5 * Math.sin(localPhase * Math.PI * 2);
      const vertical = (7.5 + 3 * twinkle) * s;
      const horizontal = (5.5 + 2 * twinkle) * s;
      const waist = Math.max(1.5, 2.2 * s);

      ctx.save();
      ctx.shadowBlur = (9 + 7 * twinkle) * s;
      ctx.shadowColor = `rgba(255, 232, 218, ${0.72 + 0.24 * twinkle})`;
      ctx.fillStyle = `rgba(255, 252, 248, ${0.86 + 0.12 * twinkle})`;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y - vertical);
      ctx.quadraticCurveTo(p.x + waist, p.y - waist, p.x + horizontal, p.y);
      ctx.quadraticCurveTo(p.x + waist, p.y + waist, p.x, p.y + vertical);
      ctx.quadraticCurveTo(p.x - waist, p.y + waist, p.x - horizontal, p.y);
      ctx.quadraticCurveTo(p.x - waist, p.y - waist, p.x, p.y - vertical);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = "rgba(255, 255, 255, 0.98)";
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(1.3, 1.7 * s), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else if (motion === "wiggle") {
      // Vertical lash-wisp — two short wiggling strokes upward
      ctx.shadowBlur = 8 * s;
      ctx.shadowColor = "rgba(245, 220, 215, 0.9)";
      ctx.strokeStyle = "rgba(255, 248, 244, 0.95)";
      ctx.lineWidth = 1.6 * s;
      const len = 18 * s;
      const wig = 2.4 * s * Math.sin(t / 90 + idx);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.quadraticCurveTo(p.x + wig, p.y - len * 0.5, p.x, p.y - len);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(p.x - 3 * s, p.y);
      ctx.quadraticCurveTo(p.x - 3 * s - wig, p.y - len * 0.4, p.x - 4 * s, p.y - len * 0.85);
      ctx.stroke();
      ctx.shadowBlur = 0;
    } else if (motion === "swirl") {
      // Animated spiral — communicates "circle outward"
      ctx.strokeStyle = `rgba(255, 248, 244, ${0.85})`;
      ctx.lineWidth = 1.8 * s;
      ctx.shadowBlur = 14 * s;
      ctx.shadowColor = "rgba(213, 168, 164, 0.9)";
      const spinR = 11 * s + 1.5 * s * pulse;
      const a0 = (t / 600) % (Math.PI * 2);
      ctx.beginPath();
      ctx.arc(p.x, p.y, spinR, a0, a0 + Math.PI * 1.4);
      ctx.stroke();
      ctx.shadowBlur = 0;
    } else {
      // Default tap / sparkle / stipple: a breathing dot with halo
      // Outer halo
      ctx.beginPath();
      ctx.arc(p.x, p.y, dotR + 6 * s, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(213, 168, 164, ${0.18 + 0.18 * pulse})`;
      ctx.fill();
      // Inner bright dot
      ctx.beginPath();
      ctx.arc(p.x, p.y, dotR, 0, Math.PI * 2);
      ctx.shadowBlur = 14 * s;
      ctx.shadowColor = "rgba(245, 220, 215, 0.95)";
      ctx.fillStyle = `rgba(255, 248, 244, ${0.85 + 0.1 * pulse})`;
      ctx.fill();
      ctx.shadowBlur = 0;

      // Sparkle cross for highlighter / sweep — adds shimmer
      if (motion === "tap" || motion === "sweep") {
        const sp = 9 * s + 2 * s * pulse;
        ctx.strokeStyle = `rgba(255, 248, 244, ${0.7 + 0.2 * pulse})`;
        ctx.lineWidth = 1.2 * s;
        ctx.beginPath();
        ctx.moveTo(p.x - sp, p.y); ctx.lineTo(p.x + sp, p.y);
        ctx.moveTo(p.x, p.y - sp); ctx.lineTo(p.x, p.y + sp);
        ctx.stroke();
      }
    }
  }
}
